export type User = {
  displayName: string;
  email: string;
  globalRole: 'SUPER_ADMIN' | null;
  id: string;
};

export type MfaState = { enrolled: boolean; requiredForRole: boolean; verified: boolean };
export type SessionResponse = { csrfToken: string; mfa: MfaState; user: User };
export type MfaChallenge = {
  challengeToken: string;
  expiresAt: string;
  mfaRequired: true;
};
export type SystemStatus = {
  checks: { d1: string; mfaEncryption: string; r2: string; workflow: string };
  environment: string;
};

export type Company = {
  address: string | null;
  companyCode: string;
  contactEmail: string | null;
  contactName: string | null;
  contractStartedOn: string;
  currentRole?: 'COMPANY_ADMIN' | 'SITE_USER' | null;
  id: string;
  name: string;
  nameKana: string | null;
  phone: string | null;
  remarks: string | null;
  status: 'ACTIVE' | 'SUSPENDED';
  version: number;
};

export type CompanyUser = {
  displayName: string;
  email: string;
  joinedAt: string | null;
  role: 'COMPANY_ADMIN' | 'SITE_USER';
  status: 'INVITED' | 'ACTIVE' | 'SUSPENDED';
  userId: string;
};

export type ProjectAssignment = { displayName: string; email: string; userId: string };

export type CatalogItem = {
  code: string;
  id: string;
  name: string;
  sortOrder: number;
  status: 'ACTIVE' | 'INACTIVE';
};

export type Project = {
  actualCompletedOn: string | null;
  calculatedTrainingCount: number;
  chiefEngineerName: string | null;
  clientProfileType:
    'HOKKAIDO_DEVELOPMENT_BUREAU' | 'HOKKAIDO_CONSTRUCTION_DEPARTMENT' | 'MUNICIPALITY';
  companyId: string;
  completionRequestedAt: string | null;
  contractNumber: string | null;
  deletedObjectCount: number;
  deletionStatus: 'DELETED' | 'DELETING' | 'ERROR' | 'NONE' | 'READY';
  equipmentTypeIds?: string[];
  expectedTrainingCount: number;
  expectedTrainingCountOverridden: number;
  id: string;
  issuedTrainingCount?: number;
  mainWorkSummary: string | null;
  municipalityName: string | null;
  name: string;
  plannedCompleteOn: string;
  plannedStartOn: string;
  projectCode: string;
  remainingTrainingCount?: number;
  remarks: string | null;
  requestedActualCompletedOn: string | null;
  retentionEligibleAt: string | null;
  retentionDeletedAt: string | null;
  retentionExpiresOn: string | null;
  siteManagerName: string | null;
  status: 'PLANNED' | 'ACTIVE' | 'COMPLETION_PENDING' | 'COMPLETED' | 'SUSPENDED';
  supervisingEngineerName: string | null;
  version: number;
  workTypeIds?: string[];
};

export type CompanyInput = {
  address?: string | null;
  companyCode: string;
  contactEmail?: string | null;
  contactName?: string | null;
  contractStartedOn: string;
  name: string;
  nameKana?: string | null;
  phone?: string | null;
  remarks?: string | null;
  status: 'ACTIVE' | 'SUSPENDED';
};

export type ProjectInput = {
  chiefEngineerName?: string | null;
  clientProfileType: Project['clientProfileType'];
  contractNumber?: string | null;
  equipmentTypeIds: string[];
  expectedTrainingCount?: number;
  mainWorkSummary?: string | null;
  municipalityName?: string | null;
  name: string;
  plannedCompleteOn: string;
  plannedStartOn: string;
  projectCode: string;
  remarks?: string | null;
  siteManagerName?: string | null;
  status: 'PLANNED' | 'ACTIVE' | 'SUSPENDED';
  supervisingEngineerName?: string | null;
  workTypeIds: string[];
};

export type RequestPhoto = {
  contentType: 'image/jpeg' | 'image/png' | 'image/webp';
  height: number;
  id: string;
  sizeBytes: number;
  sortOrder: number;
  width: number;
};

export type TrainingRequest = {
  companyId: string;
  createdAt: string;
  customEquipment?: string[];
  customWorkItems?: string[];
  equipmentTypeIds?: string[];
  id: string;
  inputQualityWarnings?: string[];
  nearMiss: string | null;
  occurrenceNo: number;
  photoCount: number;
  photos?: RequestPhoto[];
  priceSnapshotYen: number | null;
  projectId: string;
  remarks: string | null;
  requestedTopicText: string | null;
  returnedReason: string | null;
  reviewedAt: string | null;
  scheduledOn: string | null;
  specialAttention: string | null;
  status: 'DRAFT' | 'REQUESTED' | 'REVIEWING';
  standardMaterialOverride?: boolean;
  standardMaterialOverrideReason?: string | null;
  submittedAt: string | null;
  targetMonth: string;
  topicMode: 'AUTO' | 'REQUESTED';
  themeConditionCodes?: string[];
  updatedAt: string;
  version: number;
  workTypeIds?: string[];
};

export type TrainingRequestInput = {
  customEquipment: string[];
  customWorkItems: string[];
  equipmentTypeIds: string[];
  nearMiss?: string | null;
  occurrenceNo: number;
  remarks?: string | null;
  requestedTopicText: string;
  scheduledOn?: string | null;
  specialAttention?: string | null;
  targetMonth: string;
  themeConditionCodes: string[];
  topicMode: 'REQUESTED';
  workTypeIds: string[];
};

export type MaterialSlide = {
  body: string;
  composition: {
    diagramAssetPath: string | null;
    diagramCode: string | null;
    diagramVersionId: string | null;
    engineVersion: string;
    fitIssues: string[];
    fitStatus: 'ERROR' | 'PASS' | 'WARNING';
    layoutFamily:
      | 'CHECKLIST'
      | 'COMPARISON'
      | 'COVER_HERO'
      | 'PROCESS_FLOW'
      | 'QUIZ_FOCUS'
      | 'SOURCE_FOCUS'
      | 'SPLIT_TEXT_LEFT'
      | 'SPLIT_TEXT_RIGHT'
      | 'TEXT_FOCUS'
      | 'VISUAL_FOCUS';
    meaningfulVisual: boolean;
    regions: Array<{
      height: number;
      role: 'BODY' | 'FOOTER' | 'TITLE' | 'VISUAL';
      width: number;
      x: number;
      y: number;
    }>;
    textCharacterCount: number;
    textLineCount: number;
    visualAltText: string | null;
    visualKind:
      | 'CHECKLIST'
      | 'COMPARISON'
      | 'DECORATIVE'
      | 'DIAGRAM'
      | 'FLOW'
      | 'NONE'
      | 'PHOTO'
      | 'QUIZ'
      | 'RISK_MAP'
      | 'SOURCE';
  } | null;
  estimatedSeconds: number;
  id: string;
  mediaAssetId?: string | null;
  mediaAssetType?: 'PHOTO' | 'ILLUSTRATION' | 'VIDEO' | null;
  narration: {
    bodyCopyDetected: boolean;
    bodyOverlapScore: number;
    decisionCriterion: string;
    engineVersion: string;
    misconception: string;
    segment: 'INTRO' | 'ACCIDENT_STRUCTURE' | 'PREVENTION' | 'KY_QUIZ' | 'SUMMARY';
    siteExample: string;
    transitionText: string;
    visualGuidance: string;
  } | null;
  slideNo: number;
  speakerNotes: string;
  title: string;
  type: string;
};

export type MaterialRevision = {
  approvedForSlidesAt: string | null;
  approvalReason: string | null;
  canonicalFingerprint: string;
  createdAt: string;
  engineVersion: string;
  id: string;
  kyQuizSummary: {
    engineVersion: string;
    kyCount: number;
    packVersionId: string | null;
    qualityIssues: string[];
    qualityStatus: 'ERROR' | 'PASS';
    quizCount: number;
    quizTypeCount: number;
    sourceMode: 'KNOWLEDGE_PACK' | 'LEGACY_MODULE';
  } | null;
  kyQuestions: Array<{
    dangerPoints: string[];
    diagramCode: string | null;
    diagramKey: string | null;
    diagramVersionId: string | null;
    exclusionZone: string | null;
    modelPoints: string[];
    question: string;
    situation: string | null;
    sortOrder: number;
    sourceCode: string | null;
    spotterPosition: string | null;
    stopCondition: string | null;
    tags: string[];
    title: string | null;
    vehicleRoute: string | null;
    workerRoute: string | null;
  }>;
  narrationSummary: {
    bodyCopyCount: number;
    engineVersion: string;
    openingIdentityComplete: boolean;
    qualityIssues: string[];
    qualityStatus: 'ERROR' | 'PASS';
    segmentSeconds: Record<
      'INTRO' | 'ACCIDENT_STRUCTURE' | 'PREVENTION' | 'KY_QUIZ' | 'SUMMARY',
      number
    >;
    timedScript: string;
    totalSeconds: number;
    transitionCoverage: number;
  } | null;
  revisionNo: number;
  similarityChecks: Array<{
    comparisonScope: string;
    outcome: string;
    totalScore: number;
    variableOverlapScore: number;
  }>;
  similarityWarnings: string[];
  slides: MaterialSlide[];
  sourceType: string;
  sourceRecommendation: {
    eligibleCount: number;
    engineVersion: string;
    evaluatedCount: number;
    issueCodes: string[];
    selected: Array<{
      breakdown: {
        machine: number;
        recency: number;
        region: number;
        season: number;
        theme: number;
        workType: number;
      };
      itemId: string;
      previousMonthUsed: boolean;
      rank: number;
      reasonLabels: string[];
      title: string;
      totalScore: number;
    }>;
  } | null;
  testQuestions: Array<{
    choices: string[];
    correctChoiceNo: number;
    diagramCode: string | null;
    diagramKey: string | null;
    diagramVersionId: string | null;
    difficulty: 'ADVANCED' | 'BASIC' | 'INTERMEDIATE' | null;
    distractorRationales: string[];
    explanation: string;
    question: string;
    questionType:
      | 'CASE_DECISION'
      | 'GOOD_BAD_POSITION'
      | 'IMAGE_HAZARD'
      | 'MULTIPLE_CHOICE_4'
      | 'SEQUENCE'
      | 'TRUE_FALSE_REASON'
      | null;
    relatedItemCode: string | null;
    sortOrder: number;
    sourceCode: string | null;
    tags: string[];
  }>;
  topicId: string;
  totalEstimatedSeconds: number;
  variableOverlapScore: number;
  variantId: string;
};

export type TrainingMaterial = {
  approvedForSlidesAt: string | null;
  displayId: string;
  id: string;
  job: { errorCode: string | null; errorDetail: string | null; id: string; status: string } | null;
  revision: MaterialRevision | null;
  status: string;
};

export type GoogleConnection = {
  accountEmail: string | null;
  accountName: string | null;
  configured: boolean;
  connected: boolean;
  lastErrorCode: string | null;
  lastErrorDetail: string | null;
  lastHealthCheckedAt: string | null;
  status: 'CONNECTED' | 'ERROR' | 'REVOKED' | 'NOT_CONNECTED' | 'NOT_CONFIGURED';
};

export type GoogleSlidesTemplate = {
  driveFileId: string;
  id: string;
  name: string;
  placeholderCount: number;
  sourceModifiedTime: string;
  sourceRevisionId: string;
  sourceVersion: string;
  status: 'APPROVED' | 'RETIRED';
  templateType: 'PAPER_A4' | 'SCREEN_16_9';
};

export type GoogleSlidesExport = {
  driveFileId: string | null;
  driveWebUrl: string | null;
  errorCode: string | null;
  errorDetail: string | null;
  id: string;
  placeholderCount: number | null;
  status: 'QUEUED' | 'COPYING' | 'CONTENT_APPLIED' | 'NOTES_APPLIED' | 'VERIFIED' | 'FAILED';
  verifiedAt: string | null;
};

export type GoogleSlidesEditorAccessResult = {
  email: string;
  status: 'ADDED' | 'EXISTING' | 'OWNER' | 'SKIPPED_TEST_EMAIL' | 'UPGRADED';
};

export type PdfDelivery = {
  artifacts: Array<{
    artifactType: 'PAPER_A4_COMPLETE' | 'SAFETY_PDF_16_9';
    displayName: string;
    errorCode: string | null;
    errorDetail: string | null;
    fileId: string | null;
    pageCount: number | null;
    sha256: string | null;
    sizeBytes: number | null;
    status: 'DOWNLOADING' | 'FAILED' | 'POLLING' | 'QUEUED' | 'STARTING' | 'VERIFIED';
  }>;
  attemptCount: number;
  colorMode: 'COLOR' | 'MONOCHROME';
  errorCode: string | null;
  errorDetail: string | null;
  id: string;
  paperDeckUrl: string | null;
  sideMode: 'DUPLEX' | 'SIMPLEX';
  status:
    'BUILDING_PAPER' | 'EXPORTING_PAPER' | 'EXPORTING_SCREEN' | 'FAILED' | 'QUEUED' | 'VERIFIED';
};

export type ElectronicProfileType = Project['clientProfileType'];

export type ElectronicDeliveryProfile = {
  effectiveFrom: string;
  effectiveTo: string | null;
  id: string;
  notes: string;
  profileType: ElectronicProfileType;
  rules: {
    fileNaming: { normalization: 'NFKC'; pattern: string };
    fileSizeRules: Array<{
      artifactType: '*' | 'PAPER_A4_COMPLETE' | 'SAFETY_PDF_16_9';
      maxBytes: number;
      onExceed: 'REJECT' | 'WARN_AND_CONFIRM';
    }>;
    folders: Array<{
      allowedArtifactTypes: Array<'PAPER_A4_COMPLETE' | 'SAFETY_PDF_16_9'>;
      path: string;
    }>;
    requiredNoticeFiles: string[];
    rootFolderTemplate: string;
    schemaVersion: 1;
    validationRules: string[];
  };
  sourceUrl: string;
  status: 'APPROVED' | 'DRAFT' | 'IN_REVIEW' | 'RETIRED';
  updatedAt: string;
  verifiedAt: string | null;
  verifiedByName: string | null;
  version: string;
};

export type ElectronicDelivery = {
  effectiveProfile: ElectronicDeliveryProfile | null;
  job: {
    attemptCount: number;
    displayName: string | null;
    errorCode: string | null;
    errorDetail: string | null;
    fileId: string | null;
    id: string;
    manifest: Record<string, unknown> | null;
    profileType: ElectronicProfileType;
    profileVersion: string;
    sizeBytes: number | null;
    status: 'BUILDING' | 'FAILED' | 'VERIFIED';
    validationReport: Record<string, unknown> | null;
    verifiedAt: string | null;
  } | null;
  municipalityName: string | null;
  requiredProfileType: ElectronicProfileType;
  targetMonth: string;
};

export type VideoDelivery = {
  publication: {
    publishedAt: string | null;
    status: 'FINAL_REVIEW' | 'PUBLISHED' | 'REVOKED' | 'WAITING_VIDEO';
    updatedAt: string;
  } | null;
  upload: {
    automatedVerifiedAt: string | null;
    deliveryFileId: string | null;
    durationSeconds: number | null;
    errorCode: string | null;
    errorDetail: string | null;
    expectedSizeBytes: number;
    expiresAt: string;
    h264AacConfirmed: boolean;
    id: string;
    identityOverlayConfirmed: boolean;
    openingNarrationConfirmed: boolean;
    originalFilename: string;
    playbackConfirmed: boolean;
    reviewNotes: string | null;
    reviewedAt: string | null;
    sha256: string | null;
    signatureVerified: boolean;
    sizeBytes: number | null;
    status:
      | 'ABORTED'
      | 'AUTOMATED_VERIFIED'
      | 'CREATED'
      | 'REJECTED'
      | 'UPLOADED'
      | 'UPLOADING'
      | 'VERIFIED';
    uploadMode: 'MULTIPART' | 'SINGLE';
  } | null;
};

export type DeliveryPortal = {
  company: { id: string; name: string };
  projects: Array<{
    id: string;
    months: Array<{
      displayId: string;
      files: Array<{
        artifactType: string;
        category: 'ELECTRONIC' | 'INTERNAL' | 'PAPER' | 'SCREEN' | 'VIDEO';
        contentType: string;
        displayName: string;
        id: string;
        sizeBytes: number;
      }>;
      materialId: string;
      publishedAt: string;
      targetMonth: string;
    }>;
    name: string;
  }>;
};

export type ProjectArchive = {
  job: {
    attemptCount: number;
    completedVolumes: number;
    createdAt: string;
    errorCode: string | null;
    errorDetail: string | null;
    id: string;
    plannedVolumes: number;
    processedBytes: number;
    processedFiles: number;
    rootFolderName: string;
    sourceManifestHash: string;
    status: 'BUILDING' | 'FAILED' | 'QUEUED' | 'VERIFIED';
    totalSourceBytes: number;
    totalSourceFiles: number;
    updatedAt: string;
    verifiedAt: string | null;
    volumeTargetBytes: number;
    volumes: Array<{
      displayName: string;
      errorCode: string | null;
      errorDetail: string | null;
      etag: string | null;
      id: string;
      peakBufferedBytes: number | null;
      sizeBytes: number | null;
      sourceBytes: number;
      sourceFileCount: number;
      status: 'FAILED' | 'PLANNED' | 'UPLOADING' | 'VERIFIED';
      verifiedAt: string | null;
      volumeNo: number;
    }>;
  } | null;
};

export type RetentionSummary = {
  deletionStatus: 'DELETED' | 'DELETING' | 'ERROR' | 'NONE' | 'READY';
  googleDrivePolicy: 'KEEP_FOR_MANUAL_REVIEW';
  job: {
    attemptCount: number;
    batchCount: number;
    deletedObjectCount: number;
    errorCode: string | null;
    errorDetail: string | null;
    finishedAt: string | null;
    id: string;
    status:
      | 'CANCELLED'
      | 'COMPLETED'
      | 'DELETING_R2'
      | 'FAILED'
      | 'QUEUED'
      | 'SCRUBBING_D1'
      | 'VERIFYING_R2';
    updatedAt: string;
  } | null;
  notices: Array<{
    createdAt: string;
    id: string;
    noticeType: 'SEVEN_DAY' | 'THIRTY_DAY';
    retentionExpiresOn: string;
    status: 'ACKNOWLEDGED' | 'ACTIVE' | 'SUPERSEDED';
  }>;
  retentionEligibleAt: string | null;
  retentionExpiresOn: string | null;
  warning: {
    daysRemaining: number;
    level: 'EXPIRED' | 'SEVEN_DAY' | 'THIRTY_DAY';
    message: string;
  } | null;
};

export type ContentStatus = 'DRAFT' | 'IN_REVIEW' | 'APPROVED' | 'RETIRED';

export type SafetyTopic = {
  code: string;
  description: string | null;
  id: string;
  name: string;
  sortOrder: number;
  status: ContentStatus;
  version: number;
};

export type SafetyMasterRevision = {
  approvalStatus: ContentStatus;
  approvedAt: string | null;
  body: string;
  category: string;
  code: string;
  contentHash: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  id: string;
  issuer: string;
  masterId: string;
  publishedOn: string | null;
  revisionNo: number;
  sourceDocument: string;
  sourceUrl: string;
  title: string;
  topicIds: string[];
  verifiedOn: string;
};

export type LatestSafetyUpdate = {
  approvalStatus: ContentStatus;
  approvedAt: string | null;
  category: string;
  checkedOn: string;
  contentHash: string;
  effectiveFrom: string;
  id: string;
  issuer: string;
  notes: string | null;
  publishedOn: string;
  sourceUrl: string;
  summary: string;
  title: string;
  topicIds: string[];
  validUntil: string;
};

export type SourceWatchSource = {
  approvalStatus: ContentStatus;
  approvedAt: string | null;
  baseUrl: string | null;
  categories: string[];
  category: string;
  checkEnabled: number;
  checkFrequencyHours: number | null;
  commercialUseStatus: 'CONFIRMED' | 'NEEDS_REVIEW' | 'PROHIBITED' | 'RESTRICTED' | null;
  consecutiveFailures: number;
  contentUsagePolicy:
    | 'CASE_SOURCE'
    | 'FACT_SOURCE'
    | 'MANUAL_PERMISSION_REQUIRED'
    | 'REFERENCE_ONLY'
    | 'STRUCTURE_REFERENCE'
    | null;
  hostname: string;
  id: string;
  issuer: string;
  lastChangedAt: string | null;
  lastCheckedAt: string | null;
  lastErrorCode: string | null;
  lastErrorDetail: string | null;
  lastErrorAt: string | null;
  lastHttpStatus: number | null;
  lastSuccessAt: string | null;
  nextCheckAt: string | null;
  organization: string | null;
  permissionNotes: string | null;
  permissionRequired: number;
  pausedAt: string | null;
  pausedReason: string | null;
  priority: 'CRITICAL' | 'HIGH' | 'NORMAL' | 'REFERENCE' | null;
  regionScope: 'HOKKAIDO' | 'MULTI_REGION' | 'NATIONAL' | 'OTHER_REGION' | null;
  registryConfigComplete: number;
  registryGroupCode: string | null;
  robotsPolicyStatus: 'ALLOWED' | 'DISALLOWED' | 'MANUAL_REVIEW' | 'UNCONFIRMED' | null;
  sourceClass:
    | 'INDUSTRY_ASSOCIATION'
    | 'OFFICIAL_GOV'
    | 'OFFICIAL_LOCAL_GOV'
    | 'PROFESSIONAL_ASSOCIATION'
    | 'REFERENCE'
    | null;
  sourceUrl: string;
  title: string;
  watchMethod: 'ATOM' | 'HTML_HASH' | 'MANUAL_INDEX' | 'PDF_METADATA' | 'RSS' | null;
};

export type CreateSourceWatchSourceInput = {
  baseUrl: string;
  categories: string[];
  checkFrequencyHours: number;
  commercialUseStatus: 'CONFIRMED' | 'NEEDS_REVIEW' | 'PROHIBITED' | 'RESTRICTED';
  contentUsagePolicy:
    | 'CASE_SOURCE'
    | 'FACT_SOURCE'
    | 'MANUAL_PERMISSION_REQUIRED'
    | 'REFERENCE_ONLY'
    | 'STRUCTURE_REFERENCE';
  issuer: string;
  organization: string;
  permissionNotes: string | null;
  permissionRequired: boolean;
  priority: 'CRITICAL' | 'HIGH' | 'NORMAL' | 'REFERENCE';
  regionScope: 'HOKKAIDO' | 'MULTI_REGION' | 'NATIONAL' | 'OTHER_REGION';
  registryGroupCode: string;
  robotsPolicyStatus: 'ALLOWED' | 'DISALLOWED' | 'MANUAL_REVIEW' | 'UNCONFIRMED';
  sourceClass:
    | 'INDUSTRY_ASSOCIATION'
    | 'OFFICIAL_GOV'
    | 'OFFICIAL_LOCAL_GOV'
    | 'PROFESSIONAL_ASSOCIATION'
    | 'REFERENCE';
  sourceUrl: string;
  title: string;
  watchMethod: 'ATOM' | 'HTML_HASH' | 'MANUAL_INDEX' | 'PDF_METADATA' | 'RSS';
};

export type SourceWatchItem = {
  accidentPattern: string | null;
  administratorStatus: 'APPROVED' | 'PENDING_REVIEW' | 'REJECTED';
  canonicalItemUrl: string | null;
  category: string;
  citationLabel: string | null;
  contentHash: string;
  contentType: string | null;
  detectedAt: string;
  discoverySnippet: string | null;
  discussionPoints: string[];
  effectiveFrom: string | null;
  equipmentTypeIds: string[];
  externalId: string | null;
  firstViewedAt: string | null;
  id: string;
  isNew: boolean;
  keyPoints: string[];
  latestSafetyUpdateId: string | null;
  narrationSupport: string[];
  possibleDuplicateOfItemId: string | null;
  preventiveActions: string[];
  publishedOrUpdatedOn: string | null;
  quizCandidates: Array<{ expectedAnswer: string; question: string; rationale: string }>;
  rawTitle: string;
  rejectReasonCode:
    | 'DUPLICATE'
    | 'OTHER'
    | 'OUTDATED'
    | 'OUT_OF_SCOPE'
    | 'UNSUITABLE_FOR_TRAINING'
    | 'UNVERIFIABLE'
    | null;
  regionTags: Array<'HOKKAIDO' | 'MULTI_REGION' | 'NATIONAL' | 'OTHER_REGION'>;
  reviewNotes: string | null;
  reviewedAt: string | null;
  rootCauses: string[];
  seasonMonths: number[];
  seasonTags: Array<
    | 'AUTUMN_EARLY_DARK'
    | 'BLIZZARD'
    | 'ICY_SURFACE'
    | 'SNOW_REMOVAL'
    | 'SPRING_THAW'
    | 'SUMMER_HEAT'
    | 'WINTER_FREEZE'
    | 'YEAR_END_RUSH'
  >;
  siteApplicationPoints: string[];
  sourceId: string;
  sourceTitle: string;
  sourceUrl: string;
  summary: string | null;
  topicIds: string[];
  updatedAtSource: string | null;
  validUntil: string | null;
  workTypeIds: string[];
};

export type SourcePermissionScope =
  | 'FACTS_AND_PARAPHRASE'
  | 'ILLUSTRATION'
  | 'LAYOUT'
  | 'PDF_PAGE'
  | 'PHOTO'
  | 'SHORT_QUOTE'
  | 'TABLE_OR_CHART'
  | 'VIDEO';

export type SourcePermissionStatus =
  | 'EXPIRED'
  | 'NOT_REQUIRED_CONFIRMED'
  | 'PERMITTED'
  | 'PROHIBITED'
  | 'REQUESTED'
  | 'REQUEST_REQUIRED'
  | 'UNKNOWN';

export type SourcePermissionDecision = {
  allowsUse: boolean;
  createdAt: string;
  decidedAt: string;
  decidedBy: string;
  effectiveFrom: string | null;
  effectiveStatus: SourcePermissionStatus;
  evidenceNote: string;
  evidenceUrl: string;
  id: string;
  isCurrent: boolean;
  itemId: string;
  scope: SourcePermissionScope;
  status: SourcePermissionStatus;
  supersedesDecisionId: string | null;
  validUntil: string | null;
};

export type SourceWatchCheckResult = {
  contentHash: string | null;
  detectedCount: number;
  itemId: string | null;
  newItemCount: number;
  outcome: 'CHANGED' | 'FAILED' | 'NOT_MODIFIED' | 'UNCHANGED';
  sourceId: string;
};

export type SourceWatchBatch = {
  completedAt: string | null;
  deferredCount: number;
  eligibleCount: number;
  failureCount: number;
  id: string;
  maxSources: number;
  newItemCount: number;
  processedCount: number;
  selectedCount: number;
  startedAt: string;
  status: 'COMPLETED' | 'FAILED' | 'PARTIAL_FAILED' | 'RUNNING';
  successCount: number;
  timeBudgetMs: number;
  triggerType: 'MANUAL' | 'SCHEDULED';
};

export type TrainingModule = {
  code: string;
  currentVersionId: string | null;
  id: string;
  moduleType: string;
  status: ContentStatus;
};

export type TrainingModuleVersion = {
  approvalStatus: ContentStatus;
  approvedAt: string | null;
  body: string;
  code: string;
  contentHash: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  id: string;
  moduleId: string;
  moduleType: string;
  required: number;
  speakerNotes: string;
  title: string;
  topicIds: string[];
  versionNo: number;
};

export type ThemeKnowledgePackVersionInput = {
  items: Array<{
    body: string;
    code: string;
    conditionCode?:
      | 'EXCAVATOR_PROXIMITY'
      | 'LOADING_AREA'
      | 'NARROW_AREA'
      | 'NIGHT_OR_TWILIGHT'
      | 'PUBLIC_ROAD_ENTRANCE'
      | 'PUBLIC_TRAFFIC_CROSSING'
      | 'ROAD_SHOULDER'
      | 'SLOPE'
      | 'SPOTTER_PRESENT'
      | 'TURNING_SPACE'
      | 'UNLOADING_AREA'
      | 'WORKER_CROSSING'
      | null;
    section:
      | 'ACCIDENT_PATTERN'
      | 'BAD_EXAMPLE'
      | 'DIAGRAM_REQUIREMENT'
      | 'DURING_WORK_RULE'
      | 'ENVIRONMENT_FACTOR'
      | 'EQUIPMENT_FACTOR'
      | 'GOOD_EXAMPLE'
      | 'HUMAN_FACTOR'
      | 'PRE_WORK_CHECK'
      | 'PRIMARY_HAZARD'
      | 'RECOMMENDED_VISUAL'
      | 'ROLE_DRIVER'
      | 'ROLE_SPOTTER'
      | 'ROLE_WORKER'
      | 'ROOT_CAUSE'
      | 'STOP_WORK_CONDITION';
    title: string;
  }>;
  kyScenarios: Array<{
    code: string;
    dangerPoints: string[];
    diagramKey?: string | null;
    exclusionZone: string;
    modelPoints: string[];
    prompt: string;
    relatedItemCode: string;
    situation: string;
    spotterPosition: string;
    stopCondition: string;
    tags: string[];
    title: string;
    vehicleRoute: string;
    workerRoute: string;
  }>;
  quizBank: Array<{
    choices: string[];
    code: string;
    correctAnswer: string;
    diagramKey?: string | null;
    difficulty: 'ADVANCED' | 'BASIC' | 'INTERMEDIATE';
    distractorRationales: string[];
    explanation: string;
    question: string;
    relatedItemCode: string;
    tags: string[];
    type:
      | 'CASE_DECISION'
      | 'GOOD_BAD_POSITION'
      | 'IMAGE_HAZARD'
      | 'MULTIPLE_CHOICE_4'
      | 'SEQUENCE'
      | 'TRUE_FALSE_REASON';
  }>;
  sources: Array<{
    latestSafetyUpdateId?: string | null;
    safetyMasterRevisionId?: string | null;
  }>;
  themeOverview: string;
  title: string;
  variations: Array<{
    guidance: string[];
    key: string;
    title: string;
    type: 'SEASON' | 'WORK_TYPE';
  }>;
  whyItMatters: string;
};

export type ThemeKnowledgePackVersion = ThemeKnowledgePackVersionInput & {
  approvalStatus: ContentStatus;
  approvedAt: string | null;
  code: string;
  contentHash: string;
  id: string;
  packId: string;
  packStatus: ContentStatus;
  topicId: string;
  topicName: string;
  versionNo: number;
};

export type DiagramAssetVersionInput = {
  altText: string;
  annotationAnchors: Array<{
    code: string;
    kind: 'CHECK' | 'DANGER' | 'ROLE' | 'ROUTE' | 'SAFE' | 'STOP';
    label: string;
    x: number;
    y: number;
  }>;
  aspectRatio: '16:9';
  assetPath: string;
  diagramType:
    | 'BLIND_SPOT_TOP'
    | 'BLIND_SPOT_SIDE'
    | 'SPOTTER_POSITION'
    | 'VEHICLE_ROUTE'
    | 'EXCLUSION_ZONE'
    | 'EQUIPMENT_INTERACTION'
    | 'PUBLIC_TRAFFIC'
    | 'NARROW_AREA'
    | 'GOOD_BAD_COMPARISON'
    | 'STOP_DECISION'
    | 'WEATHER_VARIANT';
  purpose: string;
  rightsNote: string;
  sourceUrl?: string | null;
  title: string;
  variant: 'STANDARD' | 'SAFE' | 'DANGER' | 'COMPARISON' | 'NIGHT_SNOW';
};

export type DiagramAssetVersion = DiagramAssetVersionInput & {
  aliases: string[];
  approvalStatus: ContentStatus;
  approvedAt: string | null;
  assetId: string;
  assetStatus: ContentStatus;
  code: string;
  contentHash: string;
  id: string;
  sourceUrl: string | null;
  topicId: string;
  topicName: string;
  versionNo: number;
};

export type MaterialVariant = {
  code: 'A' | 'B' | 'C' | 'D' | 'E';
  description: string;
  id: string;
  layoutRules: unknown;
  name: string;
  selectionRules: unknown;
  status: ContentStatus;
  topicId: string;
  topicName: string;
  version: number;
};

export type VariantCoverage = {
  approvedCodes: string[];
  missingApprovedCodes: string[];
  missingCodes: string[];
  topicId: string;
  topicName: string;
};

export type MediaLibraryAsset = {
  assetType: 'PHOTO' | 'ILLUSTRATION' | 'VIDEO';
  category: string | null;
  contentType: string;
  createdAt: string;
  durationMs: number | null;
  height: number | null;
  id: string;
  licenseNotes: string | null;
  reviewStatus: ContentStatus | null;
  sha256: string;
  sizeBytes: number;
  sourceUrl: string | null;
  title: string | null;
  width: number | null;
};

type ProblemDetails = {
  code?: string;
  detail?: string;
  requestId?: string;
};

export class ApiError extends Error {
  public constructor(
    message: string,
    public readonly status: number,
    public readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function responseBody<T>(response: Response): Promise<T> {
  if (response.ok) return (await response.json()) as T;
  let problem: ProblemDetails = {};
  try {
    problem = (await response.json()) as ProblemDetails;
  } catch {
    // The generic message below intentionally avoids exposing raw upstream bodies.
  }
  throw new ApiError(problem.detail ?? '通信に失敗しました。', response.status, problem.requestId);
}

export async function getBootstrapStatus(): Promise<{ requiresBootstrap: boolean }> {
  return responseBody(
    await fetch('/api/v1/bootstrap/status', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }),
  );
}

export async function restoreSession(): Promise<SessionResponse> {
  return responseBody(
    await fetch('/api/v1/auth/session', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }),
  );
}

export async function login(
  email: string,
  password: string,
): Promise<SessionResponse | MfaChallenge> {
  return responseBody(
    await fetch('/api/v1/auth/login', {
      body: JSON.stringify({ email, password }),
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      method: 'POST',
    }),
  );
}

export async function verifyMfaLogin(
  challengeToken: string,
  code: string,
): Promise<SessionResponse> {
  return apiJson<SessionResponse>('/api/v1/auth/mfa/verify-login', {
    body: { challengeToken, code },
    method: 'POST',
  });
}

export async function startMfaEnrollment(
  currentPassword: string,
  csrfToken: string,
): Promise<{ accountName: string; manualKey: string; otpauthUri: string }> {
  return apiJson('/api/v1/auth/mfa/enroll', {
    body: { currentPassword },
    csrfToken,
    method: 'POST',
  });
}

export async function confirmMfaEnrollment(
  code: string,
  csrfToken: string,
): Promise<{ mfa: MfaState; recoveryCodes: string[] }> {
  return apiJson('/api/v1/auth/mfa/confirm', {
    body: { code },
    csrfToken,
    method: 'POST',
  });
}

export async function logout(csrfToken: string): Promise<void> {
  await responseBody(
    await fetch('/api/v1/auth/logout', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken },
      method: 'POST',
    }),
  );
}

export async function updateOwnAccount(
  input: { currentPassword: string; email?: string; newPassword?: string },
  csrfToken: string,
): Promise<SessionResponse> {
  return apiJson<SessionResponse>('/api/v1/auth/account', {
    body: input,
    csrfToken,
    method: 'PATCH',
  });
}

export async function resetOwnPasswordWithMfa(
  input: { code: string; newPassword: string },
  csrfToken: string,
): Promise<SessionResponse> {
  return apiJson<SessionResponse>('/api/v1/auth/account/password-reset-with-mfa', {
    body: input,
    csrfToken,
    method: 'POST',
  });
}

export async function resetPasswordWithMfa(input: {
  code: string;
  email: string;
  newPassword: string;
}): Promise<{ reset: true }> {
  return apiJson<{ reset: true }>('/api/v1/auth/password-reset-with-mfa', {
    body: input,
    method: 'POST',
  });
}

export async function getSystemStatus(): Promise<SystemStatus> {
  return responseBody(
    await fetch('/api/v1/admin/system', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }),
  );
}

async function apiJson<T>(
  path: string,
  options: {
    body?: unknown;
    csrfToken?: string;
    idempotencyKey?: string;
    method?: string;
  } = {},
): Promise<T> {
  return responseBody(
    await fetch(path, {
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        ...(options.body === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...(options.csrfToken === undefined ? {} : { 'X-CSRF-Token': options.csrfToken }),
        ...(options.idempotencyKey === undefined
          ? {}
          : { 'Idempotency-Key': options.idempotencyKey }),
      },
      method: options.method ?? 'GET',
    }),
  );
}

export async function listCompanies(): Promise<Company[]> {
  const companies: Company[] = [];
  let cursor: string | null = null;
  do {
    const page: { companies: Company[]; nextCursor: string | null } = await apiJson(
      `/api/v1/companies?limit=100${cursor === null ? '' : `&cursor=${encodeURIComponent(cursor)}`}`,
    );
    companies.push(...page.companies);
    cursor = page.nextCursor;
  } while (cursor !== null);
  return companies;
}

export async function createCompany(input: CompanyInput, csrfToken: string): Promise<Company> {
  return (
    await apiJson<{ company: Company }>('/api/v1/companies', {
      body: input,
      csrfToken,
      method: 'POST',
    })
  ).company;
}

export async function updateCompany(
  companyId: string,
  input: Partial<CompanyInput> & { version: number },
  csrfToken: string,
): Promise<Company> {
  return (
    await apiJson<{ company: Company }>(`/api/v1/companies/${companyId}`, {
      body: input,
      csrfToken,
      method: 'PATCH',
    })
  ).company;
}

export async function archiveCompany(
  companyId: string,
  version: number,
  reason: string,
  csrfToken: string,
): Promise<void> {
  await apiJson(`/api/v1/companies/${companyId}`, {
    body: { reason, version },
    csrfToken,
    method: 'DELETE',
  });
}

export async function listCompanyUsers(companyId: string): Promise<CompanyUser[]> {
  return (await apiJson<{ users: CompanyUser[] }>(`/api/v1/companies/${companyId}/users`)).users;
}

export async function inviteCompanyUser(
  companyId: string,
  input: { displayName: string; email: string; role: CompanyUser['role'] },
  csrfToken: string,
): Promise<{ invitationToken: string | null; user: CompanyUser }> {
  return apiJson(`/api/v1/companies/${companyId}/users`, {
    body: input,
    csrfToken,
    method: 'POST',
  });
}

export async function updateCompanyUser(
  companyId: string,
  userId: string,
  input: { role: CompanyUser['role']; status: CompanyUser['status'] },
  csrfToken: string,
): Promise<CompanyUser> {
  return (
    await apiJson<{ user: CompanyUser }>(`/api/v1/companies/${companyId}/users/${userId}`, {
      body: input,
      csrfToken,
      method: 'PATCH',
    })
  ).user;
}

export async function removeCompanyUser(
  companyId: string,
  userId: string,
  reason: string,
  csrfToken: string,
): Promise<void> {
  await apiJson(`/api/v1/companies/${companyId}/users/${userId}`, {
    body: { reason },
    csrfToken,
    method: 'DELETE',
  });
}

export async function listCatalog(kind: 'equipment-types' | 'work-types'): Promise<CatalogItem[]> {
  return (await apiJson<{ items: CatalogItem[] }>(`/api/v1/catalogs/${kind}`)).items;
}

export async function createCatalogItem(
  kind: 'equipment-types' | 'work-types',
  input: { code: string; name: string },
  csrfToken: string,
): Promise<CatalogItem> {
  return (
    await apiJson<{ item: CatalogItem }>(`/api/v1/catalogs/${kind}`, {
      body: input,
      csrfToken,
      method: 'POST',
    })
  ).item;
}

export async function updateCatalogItem(
  kind: 'equipment-types' | 'work-types',
  itemId: string,
  input: { name: string; status: 'ACTIVE' | 'INACTIVE' },
  csrfToken: string,
): Promise<CatalogItem> {
  return (
    await apiJson<{ item: CatalogItem }>(`/api/v1/catalogs/${kind}/${itemId}`, {
      body: input,
      csrfToken,
      method: 'PATCH',
    })
  ).item;
}

export async function listProjects(companyId: string): Promise<Project[]> {
  const projects: Project[] = [];
  let cursor: string | null = null;
  do {
    const page: { nextCursor: string | null; projects: Project[] } = await apiJson(
      `/api/v1/companies/${companyId}/projects?limit=100${cursor === null ? '' : `&cursor=${encodeURIComponent(cursor)}`}`,
    );
    projects.push(...page.projects);
    cursor = page.nextCursor;
  } while (cursor !== null);
  return projects;
}

export async function getProject(companyId: string, projectId: string): Promise<Project> {
  return (
    await apiJson<{ project: Project }>(`/api/v1/companies/${companyId}/projects/${projectId}`)
  ).project;
}

export async function createProject(
  companyId: string,
  input: ProjectInput,
  csrfToken: string,
): Promise<Project> {
  return (
    await apiJson<{ project: Project }>(`/api/v1/companies/${companyId}/projects`, {
      body: input,
      csrfToken,
      method: 'POST',
    })
  ).project;
}

export async function updateProject(
  companyId: string,
  projectId: string,
  input: Partial<ProjectInput> & { version: number },
  csrfToken: string,
): Promise<Project> {
  return (
    await apiJson<{ project: Project }>(`/api/v1/companies/${companyId}/projects/${projectId}`, {
      body: input,
      csrfToken,
      method: 'PATCH',
    })
  ).project;
}

export async function archiveProject(
  companyId: string,
  projectId: string,
  version: number,
  reason: string,
  csrfToken: string,
): Promise<void> {
  await apiJson(`/api/v1/companies/${companyId}/projects/${projectId}`, {
    body: { reason, version },
    csrfToken,
    method: 'DELETE',
  });
}

export async function requestProjectCompletion(
  companyId: string,
  projectId: string,
  actualCompletedOn: string,
  csrfToken: string,
): Promise<Project> {
  return (
    await apiJson<{ project: Project }>(
      `/api/v1/companies/${companyId}/projects/${projectId}/completion-request`,
      { body: { actualCompletedOn }, csrfToken, method: 'POST' },
    )
  ).project;
}

export async function confirmProjectCompletion(
  companyId: string,
  projectId: string,
  input: {
    actualCompletedOn?: string;
    confirmRetentionShortening?: boolean;
    reason?: string | null;
  },
  csrfToken: string,
): Promise<Project> {
  return (
    await apiJson<{ project: Project }>(
      `/api/v1/companies/${companyId}/projects/${projectId}/completion-confirmation`,
      { body: input, csrfToken, method: 'POST' },
    )
  ).project;
}

export async function listProjectAssignments(
  companyId: string,
  projectId: string,
): Promise<ProjectAssignment[]> {
  return (
    await apiJson<{ users: ProjectAssignment[] }>(
      `/api/v1/companies/${companyId}/projects/${projectId}/users`,
    )
  ).users;
}

export async function assignProjectUser(
  companyId: string,
  projectId: string,
  userId: string,
  csrfToken: string,
): Promise<void> {
  await apiJson(`/api/v1/companies/${companyId}/projects/${projectId}/users`, {
    body: { userId },
    csrfToken,
    method: 'POST',
  });
}

export async function unassignProjectUser(
  companyId: string,
  projectId: string,
  userId: string,
  csrfToken: string,
): Promise<void> {
  await apiJson(`/api/v1/companies/${companyId}/projects/${projectId}/users/${userId}`, {
    csrfToken,
    method: 'DELETE',
  });
}

export async function listTrainingRequests(
  companyId: string,
  projectId: string,
): Promise<TrainingRequest[]> {
  const requests: TrainingRequest[] = [];
  let cursor: string | null = null;
  do {
    const page: { nextCursor: string | null; requests: TrainingRequest[] } = await apiJson(
      `/api/v1/companies/${companyId}/projects/${projectId}/training-requests?limit=100${cursor === null ? '' : `&cursor=${encodeURIComponent(cursor)}`}`,
    );
    requests.push(...page.requests);
    cursor = page.nextCursor;
  } while (cursor !== null);
  return requests;
}

export async function getTrainingRequest(
  companyId: string,
  projectId: string,
  requestId: string,
): Promise<TrainingRequest> {
  return (
    await apiJson<{ trainingRequest: TrainingRequest }>(
      `/api/v1/companies/${companyId}/projects/${projectId}/training-requests/${requestId}`,
    )
  ).trainingRequest;
}

export async function createTrainingRequest(
  companyId: string,
  projectId: string,
  input: TrainingRequestInput,
  csrfToken: string,
  idempotencyKey: string,
): Promise<TrainingRequest> {
  return (
    await apiJson<{ trainingRequest: TrainingRequest }>(
      `/api/v1/companies/${companyId}/projects/${projectId}/training-requests`,
      { body: input, csrfToken, idempotencyKey, method: 'POST' },
    )
  ).trainingRequest;
}

export async function updateTrainingRequest(
  companyId: string,
  projectId: string,
  requestId: string,
  input: Partial<TrainingRequestInput> & { version: number },
  csrfToken: string,
): Promise<TrainingRequest> {
  return (
    await apiJson<{ trainingRequest: TrainingRequest }>(
      `/api/v1/companies/${companyId}/projects/${projectId}/training-requests/${requestId}`,
      { body: input, csrfToken, method: 'PATCH' },
    )
  ).trainingRequest;
}

export async function submitTrainingRequest(
  companyId: string,
  projectId: string,
  requestId: string,
  version: number,
  csrfToken: string,
  idempotencyKey: string,
): Promise<TrainingRequest> {
  return (
    await apiJson<{ trainingRequest: TrainingRequest }>(
      `/api/v1/companies/${companyId}/projects/${projectId}/training-requests/${requestId}/submit`,
      { body: { version }, csrfToken, idempotencyKey, method: 'POST' },
    )
  ).trainingRequest;
}

export async function startTrainingRequestReview(
  companyId: string,
  projectId: string,
  requestId: string,
  version: number,
  csrfToken: string,
  standardMaterialOverrideReason?: string | null,
): Promise<TrainingRequest> {
  return (
    await apiJson<{ trainingRequest: TrainingRequest }>(
      `/api/v1/companies/${companyId}/projects/${projectId}/training-requests/${requestId}/review`,
      {
        body: { standardMaterialOverrideReason: standardMaterialOverrideReason ?? null, version },
        csrfToken,
        method: 'POST',
      },
    )
  ).trainingRequest;
}

export async function returnTrainingRequest(
  companyId: string,
  projectId: string,
  requestId: string,
  version: number,
  reason: string,
  csrfToken: string,
): Promise<TrainingRequest> {
  return (
    await apiJson<{ trainingRequest: TrainingRequest }>(
      `/api/v1/companies/${companyId}/projects/${projectId}/training-requests/${requestId}/return`,
      { body: { reason, version }, csrfToken, method: 'POST' },
    )
  ).trainingRequest;
}

export async function getTrainingMaterial(
  companyId: string,
  projectId: string,
  requestId: string,
): Promise<TrainingMaterial | null> {
  return (
    await apiJson<{ material: TrainingMaterial | null }>(
      `/api/v1/companies/${companyId}/projects/${projectId}/training-requests/${requestId}/material`,
    )
  ).material;
}

export function materialSlideMediaUrl(
  companyId: string,
  projectId: string,
  revisionId: string,
  slideId: string,
): string {
  return `/api/v1/companies/${companyId}/projects/${projectId}/material-revisions/${revisionId}/slides/${slideId}/media`;
}

export async function startMaterialGeneration(
  companyId: string,
  projectId: string,
  requestId: string,
  csrfToken: string,
  idempotencyKey: string,
  similarityOverrideReason: string | null,
): Promise<{ id: string; status: string }> {
  return (
    await apiJson<{ job: { id: string; status: string } }>(
      `/api/v1/companies/${companyId}/projects/${projectId}/training-requests/${requestId}/material-generation`,
      {
        body: { similarityOverrideReason, topicId: null, variantId: null },
        csrfToken,
        idempotencyKey,
        method: 'POST',
      },
    )
  ).job;
}

export async function approveMaterialForSlides(
  companyId: string,
  projectId: string,
  revisionId: string,
  csrfToken: string,
  reason: string | null,
): Promise<void> {
  await apiJson(
    `/api/v1/companies/${companyId}/projects/${projectId}/material-revisions/${revisionId}/approve`,
    { body: { reason }, csrfToken, method: 'POST' },
  );
}

export async function getManualAiPrompt(
  companyId: string,
  projectId: string,
  requestId: string,
): Promise<string> {
  return (
    await apiJson<{ prompt: string }>(
      `/api/v1/companies/${companyId}/projects/${projectId}/training-requests/${requestId}/manual-ai-prompt`,
    )
  ).prompt;
}

export async function importManualMaterial(
  companyId: string,
  projectId: string,
  requestId: string,
  input: unknown,
  csrfToken: string,
  idempotencyKey: string,
): Promise<string> {
  return (
    await apiJson<{ revisionId: string }>(
      `/api/v1/companies/${companyId}/projects/${projectId}/training-requests/${requestId}/manual-ai-import`,
      { body: input, csrfToken, idempotencyKey, method: 'POST' },
    )
  ).revisionId;
}

export async function getGoogleConnection(): Promise<GoogleConnection> {
  return (await apiJson<{ connection: GoogleConnection }>('/api/v1/google/connection')).connection;
}

export async function startGoogleOAuth(csrfToken: string): Promise<string> {
  return (
    await apiJson<{ authorizationUrl: string }>('/api/v1/google/oauth/start', {
      csrfToken,
      method: 'POST',
    })
  ).authorizationUrl;
}

export async function checkGoogleConnection(csrfToken: string): Promise<GoogleConnection> {
  return (
    await apiJson<{ connection: GoogleConnection }>('/api/v1/google/connection/check', {
      csrfToken,
      method: 'POST',
    })
  ).connection;
}

export async function disconnectGoogle(reason: string, csrfToken: string): Promise<void> {
  await apiJson('/api/v1/google/disconnect', {
    body: { reason },
    csrfToken,
    method: 'POST',
  });
}

export async function getGooglePickerConfiguration(): Promise<{
  accessToken: string;
  appId: string;
  developerKey: string;
}> {
  return apiJson('/api/v1/google/picker-configuration');
}

export async function listGoogleSlidesTemplates(): Promise<GoogleSlidesTemplate[]> {
  return (await apiJson<{ templates: GoogleSlidesTemplate[] }>('/api/v1/google/slides-templates'))
    .templates;
}

export async function registerGoogleSlidesTemplate(
  input: {
    driveFileId: string;
    name: string;
    templateType: 'PAPER_A4' | 'SCREEN_16_9';
  },
  csrfToken: string,
): Promise<GoogleSlidesTemplate> {
  return (
    await apiJson<{ template: GoogleSlidesTemplate }>('/api/v1/google/slides-templates', {
      body: input,
      csrfToken,
      method: 'POST',
    })
  ).template;
}

export async function getPdfDelivery(
  companyId: string,
  projectId: string,
  revisionId: string,
): Promise<PdfDelivery | null> {
  return (
    await apiJson<{ delivery: PdfDelivery | null }>(
      `/api/v1/companies/${companyId}/projects/${projectId}/material-revisions/${revisionId}/google-slides/pdf-delivery`,
    )
  ).delivery;
}

export async function startPdfDelivery(
  companyId: string,
  projectId: string,
  revisionId: string,
  input: {
    colorMode: 'COLOR' | 'MONOCHROME';
    includeNarration: boolean;
    paperTemplateId: string;
    sideMode: 'DUPLEX' | 'SIMPLEX';
  },
  csrfToken: string,
  idempotencyKey: string,
): Promise<PdfDelivery> {
  return (
    await apiJson<{ delivery: PdfDelivery }>(
      `/api/v1/companies/${companyId}/projects/${projectId}/material-revisions/${revisionId}/google-slides/pdf-delivery`,
      { body: input, csrfToken, idempotencyKey, method: 'POST' },
    )
  ).delivery;
}

export function pdfDeliveryFileUrl(
  companyId: string,
  projectId: string,
  revisionId: string,
  fileId: string,
): string {
  return `/api/v1/companies/${companyId}/projects/${projectId}/material-revisions/${revisionId}/google-slides/pdf-delivery/files/${fileId}`;
}

export async function listElectronicDeliveryProfiles(): Promise<ElectronicDeliveryProfile[]> {
  return (
    await apiJson<{ profiles: ElectronicDeliveryProfile[] }>(
      '/api/v1/admin/electronic-delivery-profiles',
    )
  ).profiles;
}

export async function transitionElectronicDeliveryProfile(
  profileId: string,
  input: { action: 'APPROVE' | 'RETIRE' | 'SUBMIT_REVIEW'; reason: string },
  csrfToken: string,
): Promise<ElectronicDeliveryProfile> {
  return (
    await apiJson<{ profile: ElectronicDeliveryProfile }>(
      `/api/v1/admin/electronic-delivery-profiles/${profileId}/status`,
      { body: input, csrfToken, method: 'POST' },
    )
  ).profile;
}

export async function getElectronicDelivery(
  companyId: string,
  projectId: string,
  revisionId: string,
): Promise<ElectronicDelivery> {
  return (
    await apiJson<{ delivery: ElectronicDelivery }>(
      `/api/v1/companies/${companyId}/projects/${projectId}/material-revisions/${revisionId}/electronic-delivery`,
    )
  ).delivery;
}

export async function generateElectronicDelivery(
  companyId: string,
  projectId: string,
  revisionId: string,
  csrfToken: string,
  idempotencyKey: string,
): Promise<ElectronicDelivery> {
  return (
    await apiJson<{ delivery: ElectronicDelivery }>(
      `/api/v1/companies/${companyId}/projects/${projectId}/material-revisions/${revisionId}/electronic-delivery`,
      { body: {}, csrfToken, idempotencyKey, method: 'POST' },
    )
  ).delivery;
}

export function electronicDeliveryFileUrl(
  companyId: string,
  projectId: string,
  revisionId: string,
  fileId: string,
): string {
  return `/api/v1/companies/${companyId}/projects/${projectId}/material-revisions/${revisionId}/electronic-delivery/files/${fileId}`;
}

const videoDeliveryBase = (companyId: string, projectId: string, revisionId: string) =>
  `/api/v1/companies/${companyId}/projects/${projectId}/material-revisions/${revisionId}/video-delivery`;

export async function getVideoDelivery(
  companyId: string,
  projectId: string,
  revisionId: string,
): Promise<VideoDelivery> {
  return (
    await apiJson<{ delivery: VideoDelivery }>(videoDeliveryBase(companyId, projectId, revisionId))
  ).delivery;
}

export async function uploadDeliveryVideo(
  companyId: string,
  projectId: string,
  revisionId: string,
  file: File,
  csrfToken: string,
  onProgress: (percent: number) => void,
): Promise<VideoDelivery> {
  const base = videoDeliveryBase(companyId, projectId, revisionId);
  const created = await apiJson<{ upload: NonNullable<VideoDelivery['upload']> }>(
    `${base}/uploads`,
    {
      body: { contentType: 'video/mp4', fileName: file.name, sizeBytes: file.size },
      csrfToken,
      method: 'POST',
    },
  );
  if (created.upload.uploadMode === 'SINGLE') {
    const response = await fetch(`${base}/uploads/${created.upload.id}/content`, {
      body: file,
      credentials: 'same-origin',
      headers: { 'Content-Type': 'video/mp4', 'X-CSRF-Token': csrfToken },
      method: 'PUT',
    });
    if (!response.ok) await responseBody(response);
    onProgress(90);
  } else {
    const chunkSize = 16 * 1024 * 1024;
    let sent = 0;
    let partNumber = 1;
    while (sent < file.size) {
      const part = file.slice(sent, Math.min(sent + chunkSize, file.size));
      const response = await fetch(`${base}/uploads/${created.upload.id}/parts/${partNumber}`, {
        body: part,
        credentials: 'same-origin',
        headers: { 'X-CSRF-Token': csrfToken, 'X-Part-Size': String(part.size) },
        method: 'PUT',
      });
      if (!response.ok) await responseBody(response);
      sent += part.size;
      partNumber += 1;
      onProgress(Math.min(90, Math.round((sent / file.size) * 90)));
    }
  }
  const completed = await apiJson<{ delivery: VideoDelivery }>(
    `${base}/uploads/${created.upload.id}/complete`,
    { csrfToken, method: 'POST' },
  );
  onProgress(100);
  return completed.delivery;
}

export async function reviewDeliveryVideo(
  companyId: string,
  projectId: string,
  revisionId: string,
  uploadId: string,
  input: {
    durationSeconds: number;
    h264AacConfirmed: true;
    identityOverlayConfirmed: true;
    openingNarrationConfirmed: true;
    playbackConfirmed: true;
    reviewNotes: string | null;
  },
  csrfToken: string,
): Promise<VideoDelivery> {
  return (
    await apiJson<{ delivery: VideoDelivery }>(
      `${videoDeliveryBase(companyId, projectId, revisionId)}/uploads/${uploadId}/manual-review`,
      { body: input, csrfToken, method: 'POST' },
    )
  ).delivery;
}

export function adminVideoPreviewUrl(
  companyId: string,
  projectId: string,
  revisionId: string,
  uploadId: string,
): string {
  return `${videoDeliveryBase(companyId, projectId, revisionId)}/uploads/${uploadId}/preview`;
}

export async function publishVideoDelivery(
  companyId: string,
  projectId: string,
  revisionId: string,
  csrfToken: string,
): Promise<VideoDelivery> {
  return (
    await apiJson<{ delivery: VideoDelivery }>(
      `${videoDeliveryBase(companyId, projectId, revisionId)}/publish`,
      { csrfToken, method: 'POST' },
    )
  ).delivery;
}

export async function revokeVideoDelivery(
  companyId: string,
  projectId: string,
  revisionId: string,
  reason: string,
  csrfToken: string,
): Promise<VideoDelivery> {
  return (
    await apiJson<{ delivery: VideoDelivery }>(
      `${videoDeliveryBase(companyId, projectId, revisionId)}/revoke`,
      { body: { reason }, csrfToken, method: 'POST' },
    )
  ).delivery;
}

export async function getDeliveryPortal(companyId: string): Promise<DeliveryPortal> {
  return (await apiJson<{ portal: DeliveryPortal }>(`/api/v1/companies/${companyId}/delivery`))
    .portal;
}

export function deliveryFileUrl(companyId: string, projectId: string, fileId: string): string {
  return `/api/v1/companies/${companyId}/projects/${projectId}/delivery-files/${fileId}`;
}

export function deliveryVideoStreamUrl(
  companyId: string,
  projectId: string,
  fileId: string,
): string {
  return `${deliveryFileUrl(companyId, projectId, fileId)}/stream`;
}

function projectArchiveBase(companyId: string, projectId: string): string {
  return `/api/v1/companies/${companyId}/projects/${projectId}/project-archive`;
}

export async function getProjectArchive(
  companyId: string,
  projectId: string,
): Promise<ProjectArchive> {
  return (await apiJson<{ archive: ProjectArchive }>(projectArchiveBase(companyId, projectId)))
    .archive;
}

export async function createProjectArchive(
  companyId: string,
  projectId: string,
  csrfToken: string,
  idempotencyKey: string,
): Promise<ProjectArchive> {
  return (
    await apiJson<{ archive: ProjectArchive }>(projectArchiveBase(companyId, projectId), {
      body: {},
      csrfToken,
      idempotencyKey,
      method: 'POST',
    })
  ).archive;
}

export async function retryProjectArchive(
  companyId: string,
  projectId: string,
  jobId: string,
  csrfToken: string,
): Promise<ProjectArchive> {
  return (
    await apiJson<{ archive: ProjectArchive }>(
      `${projectArchiveBase(companyId, projectId)}/jobs/${jobId}/retry`,
      { body: {}, csrfToken, method: 'POST' },
    )
  ).archive;
}

export function projectArchiveVolumeUrl(
  companyId: string,
  projectId: string,
  jobId: string,
  volumeId: string,
): string {
  return `${projectArchiveBase(companyId, projectId)}/jobs/${jobId}/volumes/${volumeId}`;
}

function retentionBase(companyId: string, projectId: string): string {
  return `/api/v1/companies/${companyId}/projects/${projectId}/retention`;
}

export async function getProjectRetention(
  companyId: string,
  projectId: string,
): Promise<RetentionSummary> {
  return (await apiJson<{ retention: RetentionSummary }>(retentionBase(companyId, projectId)))
    .retention;
}

export async function retryProjectRetention(
  companyId: string,
  projectId: string,
  jobId: string,
  csrfToken: string,
): Promise<RetentionSummary> {
  return (
    await apiJson<{ retention: RetentionSummary }>(
      `${retentionBase(companyId, projectId)}/jobs/${jobId}/retry`,
      { body: {}, csrfToken, method: 'POST' },
    )
  ).retention;
}

export async function getGoogleSlidesExport(
  companyId: string,
  projectId: string,
  revisionId: string,
): Promise<GoogleSlidesExport | null> {
  return (
    await apiJson<{ export: GoogleSlidesExport | null }>(
      `/api/v1/companies/${companyId}/projects/${projectId}/material-revisions/${revisionId}/google-slides`,
    )
  ).export;
}

export async function startGoogleSlidesExport(
  companyId: string,
  projectId: string,
  revisionId: string,
  templateId: string,
  csrfToken: string,
  idempotencyKey: string,
): Promise<GoogleSlidesExport> {
  return (
    await apiJson<{ export: GoogleSlidesExport }>(
      `/api/v1/companies/${companyId}/projects/${projectId}/material-revisions/${revisionId}/google-slides`,
      {
        body: { templateId },
        csrfToken,
        idempotencyKey,
        method: 'POST',
      },
    )
  ).export;
}

export async function ensureGoogleSlidesEditorAccess(
  companyId: string,
  projectId: string,
  revisionId: string,
  csrfToken: string,
): Promise<GoogleSlidesEditorAccessResult> {
  return (
    await apiJson<{ editorAccess: GoogleSlidesEditorAccessResult }>(
      `/api/v1/companies/${companyId}/projects/${projectId}/material-revisions/${revisionId}/google-slides/editor-access`,
      { csrfToken, method: 'POST' },
    )
  ).editorAccess;
}

export async function uploadTrainingRequestPhoto(
  companyId: string,
  projectId: string,
  requestId: string,
  file: File,
  csrfToken: string,
): Promise<RequestPhoto> {
  const session = await apiJson<{ uploadSessionId: string; uploadUrl: string }>(
    `/api/v1/companies/${companyId}/projects/${projectId}/training-requests/${requestId}/photo-uploads`,
    {
      body: { contentType: file.type, fileName: file.name, sizeBytes: file.size },
      csrfToken,
      method: 'POST',
    },
  );
  const upload = await fetch(session.uploadUrl, {
    body: file,
    credentials: 'same-origin',
    headers: { 'Content-Type': file.type, 'X-CSRF-Token': csrfToken },
    method: 'PUT',
  });
  if (!upload.ok) await responseBody(upload);
  return (
    await apiJson<{ photo: RequestPhoto }>(`/api/v1/uploads/${session.uploadSessionId}/complete`, {
      csrfToken,
      method: 'POST',
    })
  ).photo;
}

export async function removeTrainingRequestPhoto(
  companyId: string,
  projectId: string,
  requestId: string,
  photoId: string,
  csrfToken: string,
): Promise<void> {
  await apiJson(
    `/api/v1/companies/${companyId}/projects/${projectId}/training-requests/${requestId}/photos/${photoId}`,
    { csrfToken, method: 'DELETE' },
  );
}

export function trainingRequestPhotoUrl(
  companyId: string,
  projectId: string,
  requestId: string,
  photoId: string,
): string {
  return `/api/v1/companies/${companyId}/projects/${projectId}/training-requests/${requestId}/photos/${photoId}/content`;
}

const contentBase = '/api/v1/admin/content';

export async function listSafetyTopics(): Promise<SafetyTopic[]> {
  return (await apiJson<{ topics: SafetyTopic[] }>(`${contentBase}/topics`)).topics;
}

export async function createSafetyTopic(
  input: { code: string; description: string; name: string; sortOrder: number },
  csrfToken: string,
): Promise<SafetyTopic> {
  return (
    await apiJson<{ topic: SafetyTopic }>(`${contentBase}/topics`, {
      body: input,
      csrfToken,
      method: 'POST',
    })
  ).topic;
}

export async function transitionSafetyTopic(
  topicId: string,
  status: ContentStatus,
  csrfToken: string,
): Promise<SafetyTopic> {
  return (
    await apiJson<{ topic: SafetyTopic }>(`${contentBase}/topics/${topicId}/status`, {
      body: { status },
      csrfToken,
      method: 'POST',
    })
  ).topic;
}

export type SafetyRevisionInput = {
  body: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  issuer: string;
  publishedOn?: string | null;
  sourceDocument: string;
  sourceUrl: string;
  title: string;
  topicIds: string[];
  verifiedOn: string;
};

export async function listSafetyMasterRevisions(): Promise<SafetyMasterRevision[]> {
  return (await apiJson<{ revisions: SafetyMasterRevision[] }>(`${contentBase}/safety-master`))
    .revisions;
}

export async function createSafetyMaster(
  input: { category: string; code: string; revision: SafetyRevisionInput },
  csrfToken: string,
): Promise<SafetyMasterRevision> {
  return (
    await apiJson<{ revision: SafetyMasterRevision }>(`${contentBase}/safety-master`, {
      body: input,
      csrfToken,
      method: 'POST',
    })
  ).revision;
}

export async function createSafetyMasterRevision(
  masterId: string,
  input: SafetyRevisionInput,
  csrfToken: string,
): Promise<SafetyMasterRevision> {
  return (
    await apiJson<{ revision: SafetyMasterRevision }>(
      `${contentBase}/safety-master/${masterId}/revisions`,
      { body: input, csrfToken, method: 'POST' },
    )
  ).revision;
}

export async function transitionSafetyMasterRevision(
  masterId: string,
  revisionId: string,
  status: ContentStatus,
  csrfToken: string,
): Promise<SafetyMasterRevision> {
  return (
    await apiJson<{ revision: SafetyMasterRevision }>(
      `${contentBase}/safety-master/${masterId}/revisions/${revisionId}/status`,
      { body: { status }, csrfToken, method: 'POST' },
    )
  ).revision;
}

export type LatestSafetyUpdateInput = {
  category: string;
  checkedOn: string;
  effectiveFrom: string;
  issuer: string;
  notes?: string | null;
  publishedOn: string;
  sourceUrl: string;
  summary: string;
  title: string;
  topicIds: string[];
  validUntil: string;
};

export async function listLatestSafetyUpdates(): Promise<LatestSafetyUpdate[]> {
  return (await apiJson<{ updates: LatestSafetyUpdate[] }>(`${contentBase}/latest-updates`))
    .updates;
}

export async function createLatestSafetyUpdate(
  input: LatestSafetyUpdateInput,
  csrfToken: string,
): Promise<LatestSafetyUpdate> {
  return (
    await apiJson<{ update: LatestSafetyUpdate }>(`${contentBase}/latest-updates`, {
      body: input,
      csrfToken,
      method: 'POST',
    })
  ).update;
}

export async function transitionLatestSafetyUpdate(
  updateId: string,
  status: ContentStatus,
  csrfToken: string,
): Promise<LatestSafetyUpdate> {
  return (
    await apiJson<{ update: LatestSafetyUpdate }>(
      `${contentBase}/latest-updates/${updateId}/status`,
      { body: { status }, csrfToken, method: 'POST' },
    )
  ).update;
}

export async function listSourceWatchSources(): Promise<SourceWatchSource[]> {
  return (await apiJson<{ sources: SourceWatchSource[] }>(`${contentBase}/source-watch/sources`))
    .sources;
}

export async function listSourceWatchBatches(): Promise<SourceWatchBatch[]> {
  return (await apiJson<{ batches: SourceWatchBatch[] }>(`${contentBase}/source-watch/batches`))
    .batches;
}

export async function createSourceWatchSource(
  input: CreateSourceWatchSourceInput,
  csrfToken: string,
): Promise<SourceWatchSource> {
  return (
    await apiJson<{ source: SourceWatchSource }>(`${contentBase}/source-watch/sources`, {
      body: input,
      csrfToken,
      method: 'POST',
    })
  ).source;
}

export async function transitionSourceWatchSource(
  sourceId: string,
  status: ContentStatus,
  csrfToken: string,
): Promise<SourceWatchSource> {
  return (
    await apiJson<{ source: SourceWatchSource }>(
      `${contentBase}/source-watch/sources/${sourceId}/status`,
      { body: { status }, csrfToken, method: 'POST' },
    )
  ).source;
}

export async function checkSourceWatchSource(
  sourceId: string,
  csrfToken: string,
): Promise<SourceWatchCheckResult> {
  return (
    await apiJson<{ result: SourceWatchCheckResult }>(
      `${contentBase}/source-watch/sources/${sourceId}/check`,
      { body: {}, csrfToken, method: 'POST' },
    )
  ).result;
}

export async function resumeSourceWatchSource(
  sourceId: string,
  reason: string,
  csrfToken: string,
): Promise<SourceWatchSource> {
  return (
    await apiJson<{ source: SourceWatchSource }>(
      `${contentBase}/source-watch/sources/${sourceId}/resume`,
      { body: { reason }, csrfToken, method: 'POST' },
    )
  ).source;
}

export async function listSourceWatchItems(): Promise<SourceWatchItem[]> {
  return (await apiJson<{ items: SourceWatchItem[] }>(`${contentBase}/source-watch/items`)).items;
}

export async function listSourcePermissionDecisions(): Promise<SourcePermissionDecision[]> {
  return (
    await apiJson<{ decisions: SourcePermissionDecision[] }>(
      `${contentBase}/source-watch/permissions`,
    )
  ).decisions;
}

export async function createSourcePermissionDecision(
  itemId: string,
  input: {
    effectiveFrom?: string | null;
    evidenceNote: string;
    evidenceUrl: string;
    scope: SourcePermissionScope;
    status: SourcePermissionStatus;
    supersedesDecisionId?: string | null;
    validUntil?: string | null;
  },
  csrfToken: string,
): Promise<SourcePermissionDecision> {
  return (
    await apiJson<{ decision: SourcePermissionDecision }>(
      `${contentBase}/source-watch/items/${itemId}/permissions`,
      { body: input, csrfToken, method: 'POST' },
    )
  ).decision;
}

export type SourceWatchReviewInput =
  | {
      notes?: string | null;
      reasonCode:
        | 'DUPLICATE'
        | 'OTHER'
        | 'OUTDATED'
        | 'OUT_OF_SCOPE'
        | 'UNSUITABLE_FOR_TRAINING'
        | 'UNVERIFIABLE';
      status: 'REJECTED';
    }
  | {
      accidentPattern?: string | null;
      citationLabel: string;
      discussionPoints: string[];
      effectiveFrom: string;
      equipmentTypeIds: string[];
      keyPoints: string[];
      narrationSupport: string[];
      notes?: string | null;
      preventiveActions: string[];
      publishedOrUpdatedOn: string;
      quizCandidates: Array<{ expectedAnswer: string; question: string; rationale: string }>;
      regionTags: SourceWatchItem['regionTags'];
      rootCauses: string[];
      seasonMonths: number[];
      seasonTags: SourceWatchItem['seasonTags'];
      siteApplicationPoints: string[];
      status: 'APPROVED';
      summary: string;
      topicIds: string[];
      validUntil: string;
      workTypeIds: string[];
    };

export async function markSourceWatchItemViewed(
  itemId: string,
  csrfToken: string,
): Promise<SourceWatchItem> {
  return (
    await apiJson<{ item: SourceWatchItem }>(`${contentBase}/source-watch/items/${itemId}/viewed`, {
      csrfToken,
      method: 'POST',
    })
  ).item;
}

export async function reviewSourceWatchItem(
  itemId: string,
  input: SourceWatchReviewInput,
  csrfToken: string,
): Promise<SourceWatchItem> {
  return (
    await apiJson<{ item: SourceWatchItem }>(`${contentBase}/source-watch/items/${itemId}/review`, {
      body: input,
      csrfToken,
      method: 'POST',
    })
  ).item;
}

export async function listThemeKnowledgePackVersions(): Promise<ThemeKnowledgePackVersion[]> {
  return (
    await apiJson<{ versions: ThemeKnowledgePackVersion[] }>(`${contentBase}/knowledge-packs`)
  ).versions;
}

export async function createThemeKnowledgePack(
  input: { code: string; topicId: string; version: ThemeKnowledgePackVersionInput },
  csrfToken: string,
): Promise<ThemeKnowledgePackVersion> {
  return (
    await apiJson<{ version: ThemeKnowledgePackVersion }>(`${contentBase}/knowledge-packs`, {
      body: input,
      csrfToken,
      method: 'POST',
    })
  ).version;
}

export async function createThemeKnowledgePackVersion(
  packId: string,
  input: ThemeKnowledgePackVersionInput,
  csrfToken: string,
): Promise<ThemeKnowledgePackVersion> {
  return (
    await apiJson<{ version: ThemeKnowledgePackVersion }>(
      `${contentBase}/knowledge-packs/${packId}/versions`,
      { body: input, csrfToken, method: 'POST' },
    )
  ).version;
}

export async function transitionThemeKnowledgePackVersion(
  packId: string,
  versionId: string,
  status: ContentStatus,
  csrfToken: string,
): Promise<ThemeKnowledgePackVersion> {
  return (
    await apiJson<{ version: ThemeKnowledgePackVersion }>(
      `${contentBase}/knowledge-packs/${packId}/versions/${versionId}/status`,
      { body: { status }, csrfToken, method: 'POST' },
    )
  ).version;
}

export async function listDiagramAssetVersions(): Promise<DiagramAssetVersion[]> {
  return (await apiJson<{ versions: DiagramAssetVersion[] }>(`${contentBase}/diagram-assets`))
    .versions;
}

export async function createDiagramAsset(
  input: {
    aliases: string[];
    code: string;
    topicId: string;
    version: DiagramAssetVersionInput;
  },
  csrfToken: string,
): Promise<DiagramAssetVersion> {
  return (
    await apiJson<{ version: DiagramAssetVersion }>(`${contentBase}/diagram-assets`, {
      body: input,
      csrfToken,
      method: 'POST',
    })
  ).version;
}

export async function createDiagramAssetVersion(
  assetId: string,
  input: DiagramAssetVersionInput,
  csrfToken: string,
): Promise<DiagramAssetVersion> {
  return (
    await apiJson<{ version: DiagramAssetVersion }>(
      `${contentBase}/diagram-assets/${assetId}/versions`,
      { body: input, csrfToken, method: 'POST' },
    )
  ).version;
}

export async function transitionDiagramAssetVersion(
  assetId: string,
  versionId: string,
  status: ContentStatus,
  csrfToken: string,
): Promise<DiagramAssetVersion> {
  return (
    await apiJson<{ version: DiagramAssetVersion }>(
      `${contentBase}/diagram-assets/${assetId}/versions/${versionId}/status`,
      { body: { status }, csrfToken, method: 'POST' },
    )
  ).version;
}

export type TrainingModuleVersionInput = {
  body: string;
  containsLegalClaim: boolean;
  containsNumericClaim: boolean;
  effectiveFrom: string;
  effectiveTo?: string | null;
  equipmentTypeIds: string[];
  issuer?: string | null;
  kyQuestions: string[];
  required: boolean;
  seasonFrom?: number | null;
  seasonTo?: number | null;
  sourceDocument?: string | null;
  sourceType: 'PUBLIC_SOURCE' | 'INTERNAL_EDITORIAL';
  sourceUrl?: string | null;
  speakerNotes: string;
  testQuestions: Array<{
    choices: string[];
    correctAnswer: string;
    explanation: string;
    question: string;
  }>;
  title: string;
  topicIds: string[];
  verifiedOn?: string | null;
  workTypeIds: string[];
};

export async function listTrainingModuleContent(): Promise<{
  modules: TrainingModule[];
  versions: TrainingModuleVersion[];
}> {
  return apiJson(`${contentBase}/training-modules`);
}

export async function createTrainingModuleContent(
  input: { code: string; moduleType: string; version: TrainingModuleVersionInput },
  csrfToken: string,
): Promise<TrainingModuleVersion> {
  return (
    await apiJson<{ version: TrainingModuleVersion }>(`${contentBase}/training-modules`, {
      body: input,
      csrfToken,
      method: 'POST',
    })
  ).version;
}

export async function createTrainingModuleVersion(
  moduleId: string,
  input: TrainingModuleVersionInput,
  csrfToken: string,
): Promise<TrainingModuleVersion> {
  return (
    await apiJson<{ version: TrainingModuleVersion }>(
      `${contentBase}/training-modules/${moduleId}/versions`,
      { body: input, csrfToken, method: 'POST' },
    )
  ).version;
}

export async function transitionTrainingModuleVersion(
  moduleId: string,
  versionId: string,
  status: ContentStatus,
  csrfToken: string,
): Promise<TrainingModuleVersion> {
  return (
    await apiJson<{ version: TrainingModuleVersion }>(
      `${contentBase}/training-modules/${moduleId}/versions/${versionId}/status`,
      { body: { status }, csrfToken, method: 'POST' },
    )
  ).version;
}

export async function listMaterialVariants(): Promise<MaterialVariant[]> {
  return (await apiJson<{ variants: MaterialVariant[] }>(`${contentBase}/material-variants`))
    .variants;
}

export async function listVariantCoverage(): Promise<VariantCoverage[]> {
  return (await apiJson<{ topics: VariantCoverage[] }>(`${contentBase}/variant-coverage`)).topics;
}

export async function createMaterialVariant(
  input: {
    code: MaterialVariant['code'];
    description: string;
    layoutRules: {
      introductionStyle: string;
      maximumSlides: number;
      minimumSlides: number;
      notesTone: 'DIRECT' | 'FACILITATIVE' | 'CASE_BASED';
      slotOrder: string[];
    };
    moduleRules: Array<{
      moduleId: string;
      priority: number;
      ruleType: 'REQUIRED' | 'RECOMMENDED' | 'EXCLUDED';
      slot: string;
    }>;
    name: string;
    selectionRules: {
      accidentCaseCategories: string[];
      focusRisks: string[];
      kyQuestionCategories: string[];
      mediaPolicy: 'BALANCED' | 'SITE_PHOTO_PRIORITY' | 'ILLUSTRATION_PRIORITY';
      testQuestionCategories: string[];
    };
    topicId: string;
  },
  csrfToken: string,
): Promise<MaterialVariant> {
  return (
    await apiJson<{ variant: MaterialVariant }>(`${contentBase}/material-variants`, {
      body: input,
      csrfToken,
      method: 'POST',
    })
  ).variant;
}

export async function transitionMaterialVariant(
  variantId: string,
  status: ContentStatus,
  csrfToken: string,
): Promise<MaterialVariant> {
  return (
    await apiJson<{ variant: MaterialVariant }>(
      `${contentBase}/material-variants/${variantId}/status`,
      { body: { status }, csrfToken, method: 'POST' },
    )
  ).variant;
}

export async function listMediaLibrary(): Promise<MediaLibraryAsset[]> {
  return (await apiJson<{ assets: MediaLibraryAsset[] }>(`${contentBase}/media-assets`)).assets;
}

export async function uploadMediaLibraryAsset(
  file: File,
  input: {
    assetType: MediaLibraryAsset['assetType'];
    category: string;
    licenseNotes: string;
    sourceUrl?: string | null;
    title: string;
    topicIds: string[];
  },
  csrfToken: string,
): Promise<MediaLibraryAsset> {
  const upload = await apiJson<{ uploadId: string; uploadUrl: string }>(
    `${contentBase}/media-assets/uploads`,
    {
      body: {
        ...input,
        contentType: file.type,
        fileName: file.name,
        sizeBytes: file.size,
      },
      csrfToken,
      method: 'POST',
    },
  );
  const received = await fetch(upload.uploadUrl, {
    body: file,
    credentials: 'same-origin',
    headers: { 'Content-Type': file.type, 'X-CSRF-Token': csrfToken },
    method: 'PUT',
  });
  if (!received.ok) await responseBody(received);
  return (
    await apiJson<{ asset: MediaLibraryAsset }>(
      `${contentBase}/media-assets/uploads/${upload.uploadId}/complete`,
      { csrfToken, method: 'POST' },
    )
  ).asset;
}

export async function transitionMediaLibraryAsset(
  assetId: string,
  status: ContentStatus,
  csrfToken: string,
): Promise<MediaLibraryAsset> {
  return (
    await apiJson<{ asset: MediaLibraryAsset }>(`${contentBase}/media-assets/${assetId}/status`, {
      body: { status },
      csrfToken,
      method: 'POST',
    })
  ).asset;
}

export function mediaLibraryAssetUrl(assetId: string): string {
  return `${contentBase}/media-assets/${assetId}/content`;
}
